import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  BudgetReviewDepartmentDetail,
  BudgetReviewPlanListItem,
  SetBudgetReviewCeilingRequest,
} from './budgetReviewTypes';

/**
 * The register files by STAGE, which is the reviewer's own cut.
 *
 * `status` is the shell's reserved axis — active or deleted, which Show deleted lifts — and
 * `stage` is where the plan sits in the budget cycle. Two state concepts on one record need
 * two axes, and the shell owns the name of one of them.
 */
export interface BudgetReviewListQuery extends ListQuery {
  fiscalYear?: string;
  stage?: string;
}

function toQueryString(query: BudgetReviewListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Both facets are sent whenever the KEY is present, empty string included — an empty value
    is the reader saying "none of these", which matches no row. A truthiness check would drop
    it and the server would read the axis as unmentioned, which is the opposite filter.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.fiscalYear !== undefined) params.set('fiscalYear', query.fiscalYear);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Budget Review — the verdict on a consolidated plan, and the ceilings behind it.
 *
 * **There is no `roster` here, and that is deliberate.** The list of departments under
 * review is the PLAN's own roster, `ppasApi.roster`, whose row carries the approved ceiling
 * and the deviation. The reference draws that list and the plan's PPA tab with one component
 * over one DTO; a second client here would be a second projection of one roster, and the copy
 * nobody is looking at is the one that drifts.
 *
 * **No create, no update, no delete and no clear.** A plan is not created here and cannot be
 * removed from the register — it leaves by being decided.
 */
export const budgetReviewApi = {
  list: (query: BudgetReviewListQuery, signal?: AbortSignal) =>
    api<PagedResult<BudgetReviewPlanListItem>>(`/api/budget-review${toQueryString(query)}`, {
      signal,
    }),

  /** One department's proposal fund by fund — what the ceiling modal opens on. */
  department: (budgetPlanId: string, orgUnitId: string, signal?: AbortSignal) =>
    api<BudgetReviewDepartmentDetail>(
      `/api/budget-review/${budgetPlanId}/departments/${orgUnitId}`,
      { signal },
    ),

  /** Sets one department's ceilings, every fund in one request. `budget-review.set-ceiling`. */
  setCeiling: (
    budgetPlanId: string,
    orgUnitId: string,
    request: SetBudgetReviewCeilingRequest,
  ) =>
    api<BudgetReviewDepartmentDetail>(
      `/api/budget-review/${budgetPlanId}/departments/${orgUnitId}/ceiling`,
      { method: 'PUT', body: request },
    ),

  /*
    THE THREE VERDICTS SHARE ONE KEY — `budget-review.decide`. Whoever may approve a fiscal
    year's budget may reject it and may send it back, so a key per direction would lengthen
    the permission picker without protecting anything new. Pricing a department is the
    separate privilege, because it is a working step somebody takes sixty times.
  */

  /** Reopens the submission window. Every ceiling is left exactly where it is. */
  returnForUpdate: (budgetPlanId: string) =>
    api<void>(`/api/budget-review/${budgetPlanId}/return`, { method: 'POST' }),

  /** Refused while any department's ceiling misses its ask, fund for fund. */
  approve: (budgetPlanId: string) =>
    api<void>(`/api/budget-review/${budgetPlanId}/approve`, { method: 'POST' }),

  /** No ceiling check — a rejection is disagreement with the ask, not agreement with it. */
  reject: (budgetPlanId: string) =>
    api<void>(`/api/budget-review/${budgetPlanId}/reject`, { method: 'POST' }),
};
